import { useRef } from "react";
import { FormProvider } from "react-hook-form";
import { LGoButton, LGoSheet } from "@/ui/branded";
import { useOfferForm, type OfferDraft } from "@/features/offers/hooks/useOfferForm";
import type { OfferActivityOption } from "@/data/repos/partnerOffersRepo";
import type { PartnerOffer } from "@/domain/offer/types";
import { capacityRefusal, slotHourMoved, type OfferSalesTally } from "@/domain/offer/rules";
import { endsWhenItStarts, seriesMessage } from "@/domain/offer/series";
import { OfferFormFields } from "./OfferFormFields";
import { refusedFieldMessage } from "@/lib/form-refusal";
import { offerAuthoringCopy } from "@/features/offers/offer-authoring-copy";

interface OfferFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  offer?: PartnerOffer | null;
  activities: OfferActivityOption[];
  /** A sleva being written for the first time has sold nothing. */
  sales: OfferSalesTally;
  reservedSeats: number;
  onSubmit: (draft: OfferDraft) => void | Promise<void>;
  isLoading?: boolean;
}

export function OfferFormDialog({
  open,
  onOpenChange,
  offer,
  activities,
  sales,
  reservedSeats,
  onSubmit,
  isLoading,
}: OfferFormDialogProps) {
  const isEditing = !!offer;
  const { form, buildDraft } = useOfferForm(offer ?? null, activities);
  // Publishing makes the slot and the sleva in two calls, so a second submit
  // that starts before the mutation's pending state lands would make both
  // twice. `isPending` is one rerender too late to stop it; this is not.
  const isSubmissionInFlight = useRef(false);

  const handleSubmit = form.handleSubmit(async (values) => {
    // Validate priceDiscounted ≤ activity priceOriginal at submit time.
    const selectedActivity = activities.find(
      (a) => String(a.id) === values.activity_id,
    );
    const discounted = parseFloat(values.price_discounted);
    if (
      selectedActivity?.priceOriginal != null &&
      discounted > selectedActivity.priceOriginal
    ) {
      form.setError("price_discounted", {
        message: `Zlevněná cena nesmí být vyšší než původní cena (${selectedActivity.priceOriginal} Kč)`,
      });
      return;
    }

    // `min` on the input is a browser tooltip in the browser's own language, and
    // this form's save does not pass the API that would refuse it, so the floor
    // is refused here or nowhere.
    const capacityRefused = capacityRefusal(parseInt(values.max_slots, 10), reservedSeats);
    if (capacityRefused) {
      form.setError("max_slots", { message: capacityRefused });
      return;
    }

    const draft = buildDraft(values);
    // A termín saved earlier with its end equal to its start is a 24 h one; it
    // keeps that length until the partner moves its hours.
    const isSlotNewOrMoved = !offer || slotHourMoved(offer, draft);
    if (isSlotNewOrMoved && endsWhenItStarts(values.start_time, values.end_time)) {
      form.setError("end_time", { message: seriesMessage.endEqualsStart });
      return;
    }
    if (isSlotNewOrMoved && draft.startTime.getTime() <= Date.now()) {
      form.setError("start_time", { message: offerAuthoringCopy.slotStartedAlready });
      return;
    }

    if (isSubmissionInFlight.current) return;
    isSubmissionInFlight.current = true;
    try {
      await onSubmit(draft);
    } finally {
      isSubmissionInFlight.current = false;
    }
  });

  const refusal = refusedFieldMessage(form.formState.errors);

  return (
    <LGoSheet
      open={open}
      onOpenChange={onOpenChange}
      title={isEditing ? "Upravit slevový termín" : "Nový slevový termín"}
      description={isEditing ? "Upravte detaily slevového termínu" : "Vypište last-minute slevový termín"}
      width="md"
      onSubmit={handleSubmit}
      footer={
        <>
          {refusal && (
            <p role="alert" className="mr-auto text-sm text-destructive">
              {refusal}
            </p>
          )}
          <LGoButton
            type="button"
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={isLoading}
          >
            Zrušit
          </LGoButton>
          <LGoButton type="submit" variant="primary" loading={isLoading}>
            {isEditing ? "Uložit změny" : "Vypsat slevový termín"}
          </LGoButton>
        </>
      }
    >
      <FormProvider {...form}>
        <OfferFormFields
          activities={activities}
          sales={sales}
          reservedSeats={reservedSeats}
          isEditing={isEditing}
        />
      </FormProvider>
    </LGoSheet>
  );
}
